import React, { createContext, useContext, useEffect, useState } from 'react'
import getFeatureStoreInitData from './utils/api'

/**
 * @typedef {{
 *  user_id: string,
 *  current_displayed_user_id: number,
 *  user_email: string,
 *  project_id: string,
 *  parent_account_id?: string,
 *  runtime_version: string,
 *  platform_version?: string,
 *  region?: string,
 *  environment?: string,
 *  status: boolean
 * }} InitFeatureStoreData
 */

/**
 * @typedef {{
 *  data: InitFeatureStoreData | null,
 *  isLoading: boolean,
 *  error: Error | null,
 *  updateData: (data: InitFeatureStoreData) => void,
 *  refetch: () => Promise<void>
 * }} FeatureStoreContextValue
 */

const FeatureStoreContext = createContext(/** @type {FeatureStoreContextValue | null} */ (null))

/**
 * Hook to access feature store data
 * @returns {FeatureStoreContextValue}
 */
export const useFeatureStoreData = () => {
  const context = useContext(FeatureStoreContext)
  if (!context) {
    throw new Error('useFeatureStoreData must be used within FeatureStoreDataProvider')
  }
  return context
}

/**
 * Provider that fetches and manages feature store initialization data
 * @type {React.FC}
 */
export const FeatureStoreDataProvider = ({ children }) => {
  const [data, setData] = useState(/** @type {InitFeatureStoreData | null} */ (null))
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(/** @type {Error | null} */ (null))

  const fetchData = async (signal) => {
    try {
      setIsLoading(true)
      setError(null)
      const result = await getFeatureStoreInitData(signal)
      setData(result)
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[FeatureStore] Failed to fetch init data:', err)
        setError(err)
      }
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [])

  const updateData = (newData) => {
    setData(newData)
  }

  const refetch = async () => {
    const controller = new AbortController()
    await fetchData(controller.signal)
  }

  return (
    <FeatureStoreContext.Provider value={{ data, isLoading, error, updateData, refetch }}>
      {children}
    </FeatureStoreContext.Provider>
  )
}
